import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Link as MuiLink,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  searchTextilesByPaymentId,
  type TTextilePaymentSearchResult,
} from "@/api/customs/textiles";
import { PURCHASES } from "@/config/resources";

export const PaymentSearchTable = () => {
  const [paymentId, setPaymentId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TTextilePaymentSearchResult | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleSearch = async () => {
    if (!paymentId.trim()) {
      setError("Por favor ingresa un ID de pago");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await searchTextilesByPaymentId(paymentId.trim());
      setResult(response);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al buscar el pago"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          placeholder="Ingresa el ID de pago..."
          value={paymentId}
          onChange={(e) => setPaymentId(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
        >
          Consultar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Número de Pedido
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ID Pago (Platam)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Monto Inicial
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Monto Final
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Status Pago
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Proveedor
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <MuiLink
                    href={`/${PURCHASES}/${result.reference_id}/show`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {result.reference}
                  </MuiLink>
                </TableCell>
                <TableCell>{result.id}</TableCell>
                <TableCell align="right">{formatCurrency(result.inicial_amount)}</TableCell>
                <TableCell align="right">{formatCurrency(result.final_amount)}</TableCell>
                <TableCell>{result.status}</TableCell>
                <TableCell>{result.provider}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!result && !loading && !error && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            Ingresa un ID de pago para buscar
          </Typography>
        </Box>
      )}
    </Box>
  );
};


